import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  RichTextEditorInline,
  CommentActions,
  useInlineCommentHelper,
  AddCommentTooltip,
} from '@pwc/pwc-deals-ui';
import { detect } from 'detect-browser';
import {
  CommentTarget,
  USER_ROLES,
  userTokenService,
} from '@pwc/pwc-deals-web-common';
import { useEventHub, useEventSubscription } from '@pwc/event-hub-ui';
import useAutoLock from 'common/hooks/useAutoLock';
import { useIsAddingCommentEnabled } from 'common/hooks/useIsAddingCommentEnabled';
import { useIsPreviousVersionSelected } from 'common/hooks/useIsPreviousVersionSelected';
import {
  getFakeClientUser,
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { useSyncedInfo } from '../Autosync/SyncedInfoProvider';
import { richTextFreeForm } from '../RichTextEditor/CKEditorConfig';
import { getLibraryItemsFromStore } from '../../../features/contentLibrary/libraryItemService';
import initializeCKEditor from '../RichTextEditor/initializeCKEditor';
import useCommentsAdapter from '../RichTextEditor/useCummentsAdapter';
import styles from './RichTextReadOnlyComment.module.scss';

const RichTextReadOnlyComment = ({
  richText,
  lightModeVariant,
  libraryItemId,
  commentType,
  commentTarget,
}) => {
  const { projectId } = useParams();
  const contextItem = useSyncedInfo();
  const { name = '' } = detect();
  const { isReady, currentUser } = useCommentsAdapter();
  const { isLocked } = useAutoLock();
  const isFakeClientUser = useSelector(getFakeClientUser);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const [isAddCommentVisible, setIsAddCommentVisible] = useState(false);

  const {
    hanldePointerUp,
    handleAddComment,
    hideToolbar,
    handleSelectionChange,
    handleEditorUndo,
    handleWidgetPointerUp,
    makeEditable,
    makeNonEditable,
  } = useInlineCommentHelper();

  const { eventHub } = useEventHub();
  const tipRef = useRef();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const editorRef = useRef();
  const { richTextContent } = richText || {};

  const isInlineCommentingEnabled = useIsAddingCommentEnabled();
  const isPreviousVersionSelected = useIsPreviousVersionSelected();

  const libaryItemIdentifier = useMemo(() => {
    const definition = `libraryItemId_${libraryItemId}`;

    return {
      definition,
      selector: `[data-libraryItemid="${definition}"]`,
    };
  }, [libraryItemId]);

  const rteData = () => {
    return commentTarget === CommentTarget.LibraryItem
      ? richTextContent.richTextData
      : richText;
  };

  const dispatchTipRefAction = useCallback(
    (action) => {
      if (!tipRef.current) {
        return;
      }
      action(tipRef.current);
    },
    [tipRef]
  );

  const onWidgetPointerUp = useCallback(
    (e) => {
      if (
        isInlineCommentingEnabled &&
        !isFakeClientUser &&
        !isThirdPartyMember
      ) {
        setIsAddCommentVisible(true);
        dispatchTipRefAction(() => handleWidgetPointerUp(e, tipRef.current));
      }
    },
    [
      dispatchTipRefAction,
      handleWidgetPointerUp,
      isInlineCommentingEnabled,
      isFakeClientUser,
      isThirdPartyMember,
    ]
  );

  const bindWidgetEvents = () => {
    document
      .querySelectorAll(`${libaryItemIdentifier.selector} .ck-widget`)
      .forEach((item) => {
        item.addEventListener('pointerup', onWidgetPointerUp);
      });
  };

  const isReadOnly = useMemo(
    () =>
      !isInlineCommentingEnabled ||
      isFakeClientUser ||
      isThirdPartyMember ||
      userTokenService.isPwcReadOnlyRole(projectId) ||
      isLocked,
    [
      projectId,
      isLocked,
      isInlineCommentingEnabled,
      isFakeClientUser,
      isThirdPartyMember,
    ]
  );

  const handleInitialization = (editor) => {
    const { placeholders } = getLibraryItemsFromStore(richText.data);

    initializeCKEditor({
      projectId,
      editor,
      richTextData: rteData(),
      childrenData: richText.isShared ? richText.childrenData : placeholders,
      isReadOnly: false,
      skipAutoSyncOnValidData: true,
      contextStatusInfoItem: contextItem,
      callBack: bindWidgetEvents,
      isWrapHTMLEmbed: true,
      isPwcUser,
    });
  };

  const createCommentThread = (threadId) => {
    let action = {};
    if (commentTarget === CommentTarget.LibraryItem) {
      action = {
        type: CommentActions.ADD_COMMENT_THREAD,
        payload: {
          libraryItemId,
          threadId,
          commentTarget,
          commentType,
          richTextData: editorRef?.current?.getData(),
        },
      };
    } else {
      action = {
        type: CommentActions.ADD_COMMENT_THREAD,
        payload: {
          threadId,
          commentTarget,
          commentType,
          richTextData: editorRef?.current?.getData(),
        },
      };
    }

    eventHub?.publish('content-add-comment-thread', action);
  };

  const initRTE = (editor) => {
    try {
      editorRef.current = editor;

      // Disable the WidgetTypeAround plugin for view mode
      // It should be only visible for edit mode
      const widgetTypeAroundPlugin = editor.plugins.get('WidgetTypeAround');
      widgetTypeAroundPlugin.forceDisabled('MyApplication');
      const commentsOnly = editor.plugins.get('CommentsOnly');
      const selectColumnCommand = editor.commands.get('selectTableColumn');
      const selectRowCommand = editor.commands.get('selectTableRow');

      // eslint-disable-next-line no-shadow, no-unused-vars
      commentsOnly.on('change:isEnabled', (evt, name, value, oldValue) => {
        if (value) {
          selectColumnCommand.forceDisabled('commentsOnly');
          selectRowCommand.forceDisabled('commentsOnly');
          return;
        }
        selectColumnCommand.clearForceDisabled('commentsOnly');
        selectRowCommand.clearForceDisabled('commentsOnly');
      });
      hideToolbar(editor);
      makeEditable(editor);
      handleInitialization(editor);
      bindWidgetEvents();
      makeNonEditable(true, editor);
      editor.ui.view.toolbar.element.style.display = 'none';
    } catch (e) {
      makeNonEditable(true, editor);
    }
  };

  useEventSubscription('autosave-cancel-comment-thread', () => {
    handleEditorUndo(editorRef.current);
  });

  useEffect(() => {
    const onSelectionChange = () => {
      dispatchTipRefAction(() => handleSelectionChange(tipRef.current));
    };

    document.addEventListener('selectionchange', onSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [dispatchTipRefAction, handleSelectionChange]);

  useEffect(() => {
    return () => {
      document
        .querySelectorAll(`${libaryItemIdentifier.selector} .ck-widget`)
        .forEach((item) => {
          item.removeEventListener('pointerup', onWidgetPointerUp);
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItemId]);

  const contentElement = useRef(null);

  const isAddCommentDisabled = isReadOnly;

  return (
    <div
      data-libraryItemid={libaryItemIdentifier.definition}
      ref={contentElement}
      className={classNames(
        `${styles.readonlyComment} ${
          name === 'safari' ? styles['safari-rich-text-form'] : 'rich-text-form'
        } rich-text-form--outline`,
        {
          'light-mode-variant': lightModeVariant,
        }
      )}
      onPointerUp={() => {
        if (
          isInlineCommentingEnabled &&
          !isFakeClientUser &&
          !isThirdPartyMember &&
          !userTokenService.isPwcReadOnlyRole(projectId)
        ) {
          dispatchTipRefAction(() => hanldePointerUp(tipRef.current));
        }
      }}
    >
      {isReady && (
        <RichTextEditorInline
          id={libraryItemId}
          richTextData={rteData()}
          channelId={libraryItemId}
          config={{
            ...richTextFreeForm,
            placeholder: '',
            commentsOnly: true,
            removePlugins: ['RestrictedEditingMode', 'TextTransformation'],
          }}
          onChange={() => {}}
          onBlur={() => {}}
          onFocus={() => {}}
          isFocused={false}
          label=""
          labelRef={undefined}
          onInit={initRTE}
          onAddCommentThread={createCommentThread}
          currentUser={currentUser}
          isReadOnly={
            !isInlineCommentingEnabled ||
            isFakeClientUser ||
            isThirdPartyMember ||
            userTokenService.isPwcReadOnlyRole(projectId)
          }
          isInlineCommentingEnabled={
            isInlineCommentingEnabled && !isThirdPartyMember
          }
        />
      )}

      {isInlineCommentingEnabled &&
        !isFakeClientUser &&
        !isThirdPartyMember &&
        !isPreviousVersionSelected && (
          <AddCommentTooltip
            isVisible={isAddCommentVisible}
            setIsVisible={setIsAddCommentVisible}
            isDisabled={isAddCommentDisabled}
            uniqueId={libraryItemId}
            onAddCommentClick={() => {
              if (!isAddCommentDisabled) {
                handleAddComment(editorRef.current);
              }
            }}
            contentElement={contentElement}
          />
        )}
    </div>
  );
};

export default RichTextReadOnlyComment;
